import clsx from 'clsx';
import React, {forwardRef, useMemo, useRef, useImperativeHandle} from 'react';
import {SoopInputBox, SoopDropdownItem} from '@soop-ui/soop-ui';
import styles from '@/components/help/common/FormContainer/Dropdown/Dropdown.module.scss';
import {useAutoCompleteNavigation, useDropdown} from '@/hooks/help';
import {ListOption} from '@/components/help/common/FormContainer/types';
import {useFormKeyHandler} from '@/hooks/help/useFormKeyHandler';

type SoopInputBoxProps = React.ComponentProps<typeof SoopInputBox>;
interface DropdownProps
  extends Omit<SoopInputBoxProps, 'value' | 'onChange' | 'onBlur'> {
  itemList: ListOption[];
  value?: string;
  onChange?: (v: string) => void;
  onBlur?: (e?: React.FocusEvent<any>) => void;
}

const Dropdown = forwardRef<HTMLInputElement, DropdownProps>(
  (
    {
      itemList,
      value: fieldValue,
      onChange: onFormChange,
      onBlur: onFormBlur,
      ...rest
    },
    ref,
  ) => {
    const {isDropdownOpen, dropdownAreaRef, openDropdown, closeDropdown} =
      useDropdown();

    const {handleFormKeyDown} = useFormKeyHandler();

    const localInputReference = useRef<HTMLInputElement>(null);
    useImperativeHandle(
      ref,
      () => localInputReference.current as HTMLInputElement,
    );

    const displayLabel = useMemo(() => {
      const hit = itemList.find((item) => item.value === fieldValue);
      return hit?.label ?? '';
    }, [itemList, fieldValue]);

    // 열릴 때는 항상 input에 포커스 고정
    const focusInputSoon = () =>
      requestAnimationFrame(() => localInputReference.current?.focus());

    const openAndFocus = () => {
      openDropdown();
      focusInputSoon();
    };

    const toggleDropdown = () => {
      if (isDropdownOpen) {
        closeDropdown();
      } else {
        openAndFocus();
      }
    };

    const handleSelect = (item: ListOption) => {
      onFormChange?.(item.value);
      closeDropdown();
    };

    const {listWrapperProps, listRefs, selectedIndex, onInputKeyDown} =
      useAutoCompleteNavigation<HTMLDivElement>({
        list: itemList,
        onEnterKeyUp: (item) => handleSelect(item as ListOption),
        onESCKeyUp: closeDropdown,
      });

    // wrapper에서 밖으로 나갈 때만 blur 처리
    const handleWrapperBlur = (e: React.FocusEvent<HTMLDivElement>) => {
      const next = e.relatedTarget as Node | null;
      const root = dropdownAreaRef.current;
      if (root && next && root.contains(next)) {
        return;
      }
      onFormBlur?.(e as any);
      closeDropdown();
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // 드롭다운이 닫혀있을 때만 아래 화살표로 열기
      if (!isDropdownOpen && e.key === 'ArrowDown') {
        e.preventDefault();
        openAndFocus();
        return;
      }

      handleFormKeyDown(e);

      // 기존 키보드 네비게이션 실행
      onInputKeyDown(e);
    };

    return (
      <div
        ref={dropdownAreaRef}
        className={styles.helpDropdownWrapper}
        onClick={toggleDropdown}
        onBlur={handleWrapperBlur}>
        <SoopInputBox
          ref={localInputReference}
          className={styles.inputWrapper}
          size={44}
          readOnly
          iconOnly={isDropdownOpen ? 'icoChevronUp' : 'icoChevronDown'}
          clearable={false}
          value={displayLabel}
          onKeyDown={handleInputKeyDown}
          {...rest}
        />

        {isDropdownOpen && itemList && itemList.length > 0 && (
          <div
            role='listbox'
            className={clsx(styles.DropdownList)}
            {...listWrapperProps}>
            {itemList.map((item, idx) => (
              <SoopDropdownItem
                key={item.value}
                id={item.value}
                size={44}
                mode='default'
                label={item.label}
                value={item.value}
                aria-selected={selectedIndex === idx}
                className={selectedIndex === idx ? styles.selected : undefined}
                onClick={() => {
                  handleSelect(item);
                }}
                ref={(el) => {
                  listRefs.current[idx] = el;
                }}
              />
            ))}
          </div>
        )}
      </div>
    );
  },
);

Dropdown.displayName = 'Dropdown';
export default Dropdown;
